import {
  PROTOCOL_CONSTANTS,
  type W10AwarenessRejectionReason,
} from "@klass-share/protocol";
import {
  DEFAULT_WHITEBOARD_PAGE_ID,
  HostRelativeClock,
  WhiteboardDocument,
  type WhiteboardCanvasRemoteState,
  type WhiteboardAwarenessState,
  type WhiteboardPreview,
  type WhiteboardSessionSnapshot,
} from "@klass-share/whiteboard-core";
import * as decoding from "lib0/decoding";
import * as encoding from "lib0/encoding";
import { uint32 as randomUint32 } from "lib0/random";
import { IndexeddbPersistence } from "y-indexeddb";
import * as awarenessProtocol from "y-protocols/awareness";
import * as syncProtocol from "y-protocols/sync";
import { WebsocketProvider } from "y-websocket";
import * as Y from "yjs";

import type { HostDiscovery } from "../discovery.js";
import {
  hostPresentationState,
  remoteAwarenessStates,
  WhiteboardAwarenessController,
} from "./awareness.js";

const MESSAGE_SYNC = 0;
const MESSAGE_AWARENESS = 1;
const SYNC_STEP_2 = 1;
const READY_STATE_CONNECTING = 0;
const READY_STATE_OPEN = 1;
const READY_STATE_CLOSED = 3;

export type W10LogicalLaneState = "connecting" | "open" | "closed";

export interface W10LogicalMessage {
  readonly messageId: number;
  readonly payload: Uint8Array;
}

export type W10PageSendResult =
  | { readonly status: "accepted" }
  | { readonly status: "paused" }
  | { readonly status: "local-failed"; readonly cause: W10PageLocalCause }
  | {
      readonly status: "operational-failed";
      readonly reason: "WHITEBOARD_MESSAGE_TOO_LARGE";
    }
  | {
      readonly status: "hard-protocol-failed";
      readonly reason: "Y_PROTOCOL_INTEGRITY";
    };

export interface W10WhiteboardLogicalLane {
  readonly state: W10LogicalLaneState;
  readonly writable: boolean;
  readonly lastInboundMessageId: number;
  readonly lastOutboundMessageId: number;
  send(payload: Uint8Array): W10PageSendResult;
  onMessage(listener: (message: W10LogicalMessage) => void): () => void;
  onState(listener: (state: W10LogicalLaneState) => void): () => void;
  onWritable(listener: () => void): () => void;
}

export interface W10ControlRecord {
  readonly type: string;
  readonly payload: Readonly<Record<string, unknown>>;
}

export interface W10ControlRecordLane {
  readonly state: W10LogicalLaneState;
  readonly writable: boolean;
  send(
    type: string,
    payload: Readonly<Record<string, unknown>>,
  ): W10PageSendResult;
  onRecord(listener: (record: W10ControlRecord) => void): () => void;
  onState(listener: (state: W10LogicalLaneState) => void): () => void;
  onWritable(listener: () => void): () => void;
}

export interface W10MemberCarrierIdentity {
  readonly sessionId: string;
  readonly memberLegId: string;
  readonly memberEpoch: number;
}

export interface W10MemberPrimaryCarrier {
  readonly control: W10ControlRecordLane;
  readonly whiteboard: W10WhiteboardLogicalLane;
  readonly retireForFallback?: () => void;
  readonly close: () => void;
}

export type W10PageCarrierFailure =
  | {
      readonly class: "operational";
      readonly cause: W10PageOperationalCause | null;
    }
  | { readonly class: "local"; readonly cause: W10PageLocalCause }
  | { readonly class: "policy"; readonly cause: "RATE_LIMITED" }
  | { readonly class: "hard"; readonly cause: W10PageHardCause };

export type W10PageOperationalCause =
  | "CHANNEL_OPEN_TIMEOUT"
  | "DATA_CHANNEL_BINARY_UNAVAILABLE"
  | "DTLS_OBSERVATION_UNAVAILABLE"
  | "MEMBER_READY_TIMEOUT"
  | "PRE_ADMISSION_IDLE_TIMEOUT"
  | "PRIMARY_HOST_LIVENESS_TIMEOUT"
  | "PRIMARY_ICE_DISCONNECT_TIMEOUT"
  | "REASSEMBLY_TIMEOUT"
  | "W7_RESPONSE_TIMEOUT"
  | "WHITEBOARD_MESSAGE_TOO_LARGE";

export type W10PageLocalCause =
  | "channel-closed"
  | "high-water-drain-expired"
  | "send-threw-while-open"
  | "send-progress-timeout"
  | "control-queue-overflow"
  | "awareness-client-id-changed"
  | "awareness-client-collision"
  | "awareness-clock-exhausted"
  | "counter-exhausted";

export type W10PageHardCause =
  | "CHANNEL_CONTRACT_MISMATCH"
  | "CONTROL_PROTOCOL_INTEGRITY"
  | "DTLS_CERTIFICATE_MISMATCH"
  | "DTLS_FAILURE_UNCLASSIFIED"
  | "DTLS_OBSERVER_UNSAFE"
  | "FRAME_INTEGRITY"
  | "MEMBER_READY_INTEGRITY"
  | "PRE_ADMISSION_LANE"
  | "PRE_ADMISSION_CONTROL"
  | "VERSION_MISMATCH";

export type W10WhiteboardLaneState = W10LogicalLaneState;

export type W10WhiteboardRecoverableFailure =
  | { readonly class: "local"; readonly cause: W10PageLocalCause }
  | {
      readonly class: "operational";
      readonly cause: "WHITEBOARD_MESSAGE_TOO_LARGE";
    };

export interface W10WhiteboardProviderOptions {
  readonly awareness?: awarenessProtocol.Awareness;
  readonly connect?: boolean;
  readonly onAwarenessRebuilt?: (
    previous: awarenessProtocol.Awareness,
    next: awarenessProtocol.Awareness,
  ) => void;
  readonly onControlProtocolError?: (error: Error) => void;
  readonly onProtocolError?: (error: Error) => void;
  readonly onRecoverableFailure?: (
    failure: W10WhiteboardRecoverableFailure,
  ) => void;
}

type StatusListener = (event: { status: string }) => void;
type SyncedListener = (synced: boolean) => void;
type AwarenessChanges = {
  readonly added: readonly number[];
  readonly updated: readonly number[];
  readonly removed: readonly number[];
};

type W10ResyncState =
  | "inactive"
  | "awaiting-request"
  | "awaiting-request-cut"
  | "awaiting-start-send"
  | "awaiting-start-ack"
  | "awaiting-page-step-1-send"
  | "awaiting-board-step-2"
  | "awaiting-board-step-1"
  | "awaiting-page-step-2-send"
  | "awaiting-ready"
  | "awaiting-ready-ack-send"
  | "awaiting-complete"
  | "ready";

interface W10ResyncRound {
  readonly resyncId: number;
  readonly boardDirtyRevision: number;
  readonly afterWhiteboardMessageId: number;
  pageDirtyGeneration?: number;
  pageSyncStep1MessageId?: number;
  boardSyncStep2MessageId?: number;
  boardSyncStep1MessageId?: number;
  pageSyncStep2MessageId?: number;
  pendingPageSyncStep1?: Uint8Array;
  pendingPageSyncStep2?: Uint8Array;
  requestPayload?: Readonly<Record<string, unknown>>;
  startPayload?: Readonly<Record<string, unknown>>;
  startAckPayload?: Readonly<Record<string, unknown>>;
  readyPayload?: Readonly<Record<string, unknown>>;
  readyAckPayload?: Readonly<Record<string, unknown>>;
}

interface W10PendingAwareness {
  readonly clientId: number;
  readonly clock: number;
  readonly tombstone: boolean;
}

interface W10AwarenessDisposition {
  readonly sourceWhiteboardMessageId: number;
  readonly clientId: number;
  readonly sourceClock: number;
}

interface W10AppliedAwarenessRejection {
  readonly signature: string;
  readonly disposition: W10AwarenessDisposition;
}

/**
 * Dedicated primary provider. It does not emit Yjs merely because SCTP is
 * open: the board-owned seven-record round and its final complete record own
 * both readiness gates.
 */
export class W10WhiteboardProvider {
  #awareness: awarenessProtocol.Awareness;
  readonly ws = null;
  readonly #doc: Y.Doc;
  readonly #carrier: W10MemberPrimaryCarrier;
  readonly #identity: W10MemberCarrierIdentity;
  readonly #onProtocolError: (error: Error) => void;
  readonly #onControlProtocolError: (error: Error) => void;
  readonly #onAwarenessRebuilt:
    | ((
        previous: awarenessProtocol.Awareness,
        next: awarenessProtocol.Awareness,
      ) => void)
    | undefined;
  readonly #onRecoverableFailure: (
    failure: W10WhiteboardRecoverableFailure,
  ) => void;
  readonly #statusListeners = new Set<StatusListener>();
  readonly #syncedListeners = new Set<SyncedListener>();
  #removeMessageListener: (() => void) | undefined;
  #removeWhiteboardStateListener: (() => void) | undefined;
  #removeControlListener: (() => void) | undefined;
  #removeControlStateListener: (() => void) | undefined;
  #removeWhiteboardWritableListener: (() => void) | undefined;
  #removeControlWritableListener: (() => void) | undefined;
  readonly #ownsAwareness: boolean;
  #shouldConnect = false;
  #connected = false;
  #destroyed = false;
  #synced = false;
  #state: W10ResyncState = "inactive";
  #round: W10ResyncRound | undefined;
  #lastResyncId = 0;
  #pageDirtyGeneration = 0;
  #pageClearedThrough = 0;
  #lastNotifiedGeneration = 0;
  #lastCompletePayload: Readonly<Record<string, unknown>> | undefined;
  readonly #pendingAwareness = new Map<number, W10PendingAwareness>();
  #lastAwarenessDisposition: W10AwarenessDisposition | undefined;
  #highestAwarenessSourceMessageId = 0;
  #lastAppliedAwarenessRejection: W10AppliedAwarenessRejection | undefined;
  #awarenessSubmissionCount = 0;
  #awarenessClaimMayExist = false;
  #awarenessSendInProgress = false;
  #awarenessOrdinaryGateOpened = false;
  #suppressedAwareness:
    | { readonly clientId: number; readonly clock: number }
    | undefined;

  public constructor(
    doc: Y.Doc,
    carrier: W10MemberPrimaryCarrier,
    identity: W10MemberCarrierIdentity,
    options: W10WhiteboardProviderOptions = {},
  ) {
    this.#doc = doc;
    this.#carrier = carrier;
    this.#identity = Object.freeze({ ...identity });
    this.#awareness = options.awareness ?? new awarenessProtocol.Awareness(doc);
    this.#ownsAwareness = options.awareness === undefined;
    this.#onProtocolError = options.onProtocolError ?? (() => undefined);
    this.#onControlProtocolError =
      options.onControlProtocolError ?? this.#onProtocolError;
    this.#onAwarenessRebuilt = options.onAwarenessRebuilt;
    this.#onRecoverableFailure =
      options.onRecoverableFailure ?? (() => undefined);
    this.#doc.on("update", this.#onDocumentUpdate);
    this.#awareness.on("update", this.#onAwarenessUpdate);
    if (options.connect !== false) this.connect();
  }

  public get wsconnected(): boolean {
    return this.#connected;
  }

  public get awareness(): awarenessProtocol.Awareness {
    return this.#awareness;
  }

  public get readyState(): number {
    if (this.#connected) return READY_STATE_OPEN;
    return this.#shouldConnect &&
      this.#carrier.control.state !== "closed" &&
      this.#carrier.whiteboard.state !== "closed"
      ? READY_STATE_CONNECTING
      : READY_STATE_CLOSED;
  }

  public get synced(): boolean {
    return this.#synced;
  }

  public get resyncState(): W10ResyncState {
    return this.#state;
  }

  public get dirtyGeneration(): number {
    return this.#pageDirtyGeneration;
  }

  public connect(): void {
    if (this.#destroyed || this.#shouldConnect) return;
    this.#shouldConnect = true;
    this.#removeMessageListener = this.#carrier.whiteboard.onMessage(
      this.#onWhiteboardMessage,
    );
    this.#removeWhiteboardStateListener = this.#carrier.whiteboard.onState(
      this.#onCarrierState,
    );
    this.#removeControlListener = this.#carrier.control.onRecord(
      this.#onControlRecord,
    );
    this.#removeControlStateListener = this.#carrier.control.onState(
      this.#onCarrierState,
    );
    this.#removeWhiteboardWritableListener =
      this.#carrier.whiteboard.onWritable(this.#onWhiteboardWritable);
    this.#removeControlWritableListener = this.#carrier.control.onWritable(
      this.#onControlWritable,
    );
    this.#applyCarrierState();
  }

  public disconnect(): void {
    if (!this.#shouldConnect) return;
    this.#shouldConnect = false;
    this.#removeMessageListener?.();
    this.#removeMessageListener = undefined;
    this.#removeWhiteboardStateListener?.();
    this.#removeWhiteboardStateListener = undefined;
    this.#removeControlListener?.();
    this.#removeControlListener = undefined;
    this.#removeControlStateListener?.();
    this.#removeControlStateListener = undefined;
    this.#removeWhiteboardWritableListener?.();
    this.#removeWhiteboardWritableListener = undefined;
    this.#removeControlWritableListener?.();
    this.#removeControlWritableListener = undefined;
    this.#state = "inactive";
    this.#round = undefined;
    this.#setConnected(false, true);
  }

  public destroy(): void {
    if (this.#destroyed) return;
    this.disconnect();
    this.#destroyed = true;
    this.#awareness.off("update", this.#onAwarenessUpdate);
    this.#doc.off("update", this.#onDocumentUpdate);
    if (this.#ownsAwareness) this.#awareness.destroy();
    this.#statusListeners.clear();
    this.#syncedListeners.clear();
  }

  public on(event: "status", listener: StatusListener): void {
    if (event === "status") this.#statusListeners.add(listener);
  }

  public off(event: "status", listener: StatusListener): void {
    if (event === "status") this.#statusListeners.delete(listener);
  }

  public onSynced(listener: SyncedListener): void {
    this.#syncedListeners.add(listener);
  }

  public offSynced(listener: SyncedListener): void {
    this.#syncedListeners.delete(listener);
  }

  readonly #onDocumentUpdate = (update: Uint8Array, origin: unknown): void => {
    if (origin === this) return;
    if (!this.#connected || this.#state !== "ready") {
      this.#markDirty();
      return;
    }
    const encoder = encoding.createEncoder();
    encoding.writeVarUint(encoder, MESSAGE_SYNC);
    syncProtocol.writeUpdate(encoder, update);
    const message = encoding.toUint8Array(encoder);
    try {
      validateW10YProtocolPayload(message);
    } catch (cause) {
      this.#hardFailure(cause);
      return;
    }
    const result = this.#carrier.whiteboard.send(message);
    if (result.status === "paused") {
      this.#markDirty();
      this.#state = "awaiting-request";
      this.#setSynced(false);
    } else if (result.status !== "accepted") this.#handleSendFailure(result);
  };

  readonly #onAwarenessUpdate = (
    changes: AwarenessChanges,
    origin: unknown,
  ): void => {
    if (origin === this || !this.#connected) return;
    if (!this.#ensureCurrentAwarenessId()) return;
    const localId = this.#awareness.clientID;
    if (
      changes.added.includes(localId) ||
      changes.updated.includes(localId) ||
      changes.removed.includes(localId)
    ) {
      this.#coalesceLocalAwareness();
      this.#flushPendingAwareness();
    }
  };

  readonly #onWhiteboardWritable = (): void => {
    if (!this.#connected) return;
    if (
      this.#state === "awaiting-page-step-1-send" ||
      this.#state === "awaiting-page-step-2-send"
    ) {
      this.#flushRequiredResyncWhiteboard();
      return;
    }
    this.#flushPendingAwareness();
    if (
      this.#state === "awaiting-request-cut" &&
      this.#round !== undefined &&
      !this.#pendingAwarenessPrecedesNextRound() &&
      this.#carrier.whiteboard.lastInboundMessageId ===
        this.#round.afterWhiteboardMessageId &&
      this.#carrier.whiteboard.writable
    ) {
      this.#sendResyncStart();
      return;
    }
    this.#notifyDirtyGeneration();
  };

  readonly #onControlWritable = (): void => {
    if (!this.#connected) return;
    if (
      this.#state === "awaiting-start-send" ||
      this.#state === "awaiting-ready-ack-send"
    ) {
      this.#flushRequiredResyncControl();
      return;
    }
    this.#notifyDirtyGeneration();
  };

  readonly #onCarrierState = (): void => {
    this.#applyCarrierState();
  };

  readonly #onControlRecord = (record: W10ControlRecord): void => {
    if (!this.#connected) return;
    try {
      switch (record.type) {
        case "whiteboard-resync-request":
          this.#acceptResyncRequest(record.payload);
          return;
        case "whiteboard-resync-start-ack":
          this.#acceptStartAck(record.payload);
          return;
        case "whiteboard-resync-ready":
          this.#acceptReady(record.payload);
          return;
        case "whiteboard-resync-complete":
          this.#acceptComplete(record.payload);
          return;
        case "whiteboard-awareness-rejected":
          this.#acceptAwarenessRejection(record.payload);
          return;
        case "whiteboard-resync-needed":
        case "whiteboard-resync-start":
        case "whiteboard-resync-ready-ack":
          throw new TypeError("page-originated resync record arrived inbound");
        default:
          return;
      }
    } catch (cause) {
      this.#hardFailure(cause);
    }
  };

  readonly #onWhiteboardMessage = (message: W10LogicalMessage): void => {
    if (!this.#connected) return;
    try {
      validateW10YProtocolPayload(message.payload);
      if (this.#state === "awaiting-request-cut") {
        const round = this.#requireRound();
        if (message.messageId > round.afterWhiteboardMessageId) {
          throw new TypeError("whiteboard message crossed the resync cut");
        }
        this.#applyOrdinaryMessage(message.payload, false);
        if (
          !this.#pendingAwarenessPrecedesNextRound() &&
          this.#carrier.whiteboard.lastInboundMessageId ===
            round.afterWhiteboardMessageId &&
          this.#carrier.whiteboard.writable
        ) {
          this.#sendResyncStart();
        }
        return;
      }
      if (this.#state === "awaiting-board-step-2") {
        if (syncSubtype(message.payload) !== SYNC_STEP_2) {
          throw new TypeError("resync expected board SyncStep2");
        }
        this.#applySyncMessage(message.payload, false);
        this.#requireRound().boardSyncStep2MessageId = message.messageId;
        this.#state = "awaiting-board-step-1";
        return;
      }
      if (this.#state === "awaiting-board-step-1") {
        if (syncSubtype(message.payload) !== 0) {
          throw new TypeError("resync expected board SyncStep1");
        }
        const round = this.#requireRound();
        if (message.messageId !== (round.boardSyncStep2MessageId ?? 0) + 1) {
          throw new TypeError("resync board messages are not consecutive");
        }
        const response = this.#applySyncMessage(message.payload, true);
        round.boardSyncStep1MessageId = message.messageId;
        round.pendingPageSyncStep2 = response;
        this.#state = "awaiting-page-step-2-send";
        this.#flushRequiredResyncWhiteboard();
        return;
      }
      if (this.#state === "ready") {
        this.#applyOrdinaryMessage(message.payload, true);
        return;
      }
      throw new TypeError("whiteboard message arrived outside its ready gate");
    } catch (cause) {
      this.#hardFailure(cause);
    }
  };

  #applyCarrierState(): void {
    if (!this.#shouldConnect) return;
    const open =
      this.#carrier.control.state === "open" &&
      this.#carrier.whiteboard.state === "open";
    if (!open) {
      const closed =
        this.#carrier.control.state === "closed" ||
        this.#carrier.whiteboard.state === "closed";
      this.#setConnected(false, closed);
      return;
    }
    if (this.#connected) return;
    this.#state = "awaiting-request";
    this.#setConnected(true);
    this.#notifyDirtyGeneration();
  }

  #acceptResyncRequest(payload: Readonly<Record<string, unknown>>): void {
    requireExactKeys(payload, [
      "sessionId",
      "memberLegId",
      "memberEpoch",
      "resyncId",
      "boardDirtyRevision",
      "afterWhiteboardMessageId",
    ]);
    this.#requireIdentity(payload);
    const resyncId = requirePositiveSafeInteger(payload.resyncId);
    if (this.#round !== undefined) {
      if (!sameRecord(payload, this.#round.requestPayload)) {
        throw new TypeError("resync request retry changed its payload");
      }
      if (this.#state === "awaiting-start-send")
        this.#flushRequiredResyncControl();
      return;
    }
    if (this.#state !== "awaiting-request") {
      throw new TypeError("resync request is out of phase");
    }
    if (resyncId !== this.#lastResyncId + 1) {
      throw new TypeError("resync id is not the released successor");
    }
    const boardDirtyRevision = requireNonnegativeSafeInteger(
      payload.boardDirtyRevision,
    );
    const afterWhiteboardMessageId = requireUint32(
      payload.afterWhiteboardMessageId,
    );
    if (
      afterWhiteboardMessageId < this.#carrier.whiteboard.lastInboundMessageId
    ) {
      throw new TypeError("resync request cut is stale");
    }
    this.#round = {
      resyncId,
      boardDirtyRevision,
      afterWhiteboardMessageId,
      requestPayload: Object.freeze({ ...payload }),
    };
    this.#state = "awaiting-request-cut";
    this.#flushPendingAwareness();
    if (
      !this.#pendingAwarenessPrecedesNextRound() &&
      this.#carrier.whiteboard.lastInboundMessageId ===
        afterWhiteboardMessageId &&
      this.#carrier.whiteboard.writable
    ) {
      this.#sendResyncStart();
    }
  }

  #sendResyncStart(): void {
    this.#state = "awaiting-start-send";
    this.#flushRequiredResyncControl();
  }

  #acceptStartAck(payload: Readonly<Record<string, unknown>>): void {
    requireExactKeys(payload, [
      "sessionId",
      "memberLegId",
      "memberEpoch",
      "resyncId",
      "pageDirtyGeneration",
    ]);
    this.#requireIdentity(payload);
    const round = this.#requireRound();
    if (round.startAckPayload !== undefined) {
      if (!sameRecord(payload, round.startAckPayload)) {
        throw new TypeError("resync start ACK retry changed its payload");
      }
      return;
    }
    if (
      this.#state !== "awaiting-start-ack" ||
      payload.resyncId !== round.resyncId ||
      payload.pageDirtyGeneration !== round.pageDirtyGeneration
    ) {
      throw new TypeError("resync start ACK does not echo the current start");
    }
    round.startAckPayload = Object.freeze({ ...payload });
    const encoder = encoding.createEncoder();
    encoding.writeVarUint(encoder, MESSAGE_SYNC);
    syncProtocol.writeSyncStep1(encoder, this.#doc);
    round.pendingPageSyncStep1 = encoding.toUint8Array(encoder);
    this.#state = "awaiting-page-step-1-send";
    this.#flushRequiredResyncWhiteboard();
  }

  #acceptReady(payload: Readonly<Record<string, unknown>>): void {
    const keys = [
      "sessionId",
      "memberLegId",
      "memberEpoch",
      "resyncId",
      "boardSyncStep2MessageId",
      "boardSyncStep1MessageId",
      "pageSyncStep1MessageId",
      "pageSyncStep2MessageId",
      "coveredBoardDirtyRevision",
      "coveredPageDirtyGeneration",
      "boardDisposition",
      "currentBoardDirtyRevision",
    ] as const;
    requireExactKeys(payload, keys);
    this.#requireIdentity(payload);
    const round = this.#requireRound();
    if (this.#state === "awaiting-complete") {
      if (!sameRecord(payload, round.readyPayload)) {
        throw new TypeError("resync ready retry changed its payload");
      }
      return;
    }
    if (this.#state === "awaiting-ready-ack-send") {
      if (!sameRecord(payload, round.readyPayload)) {
        throw new TypeError("resync ready retry changed its payload");
      }
      this.#flushRequiredResyncControl();
      return;
    }
    if (this.#state !== "awaiting-ready") {
      throw new TypeError("resync ready is out of phase");
    }
    if (
      payload.resyncId !== round.resyncId ||
      payload.boardSyncStep2MessageId !== round.boardSyncStep2MessageId ||
      payload.boardSyncStep1MessageId !== round.boardSyncStep1MessageId ||
      payload.pageSyncStep1MessageId !== round.pageSyncStep1MessageId ||
      payload.pageSyncStep2MessageId !== round.pageSyncStep2MessageId ||
      payload.coveredBoardDirtyRevision !== round.boardDirtyRevision ||
      payload.coveredPageDirtyGeneration !== round.pageDirtyGeneration
    ) {
      throw new TypeError("resync ready does not name the committed round");
    }
    for (const key of [
      "boardSyncStep2MessageId",
      "boardSyncStep1MessageId",
      "pageSyncStep1MessageId",
      "pageSyncStep2MessageId",
    ] as const) {
      requirePositiveUint32(payload[key]);
    }
    const currentBoardDirtyRevision = requireNonnegativeSafeInteger(
      payload.currentBoardDirtyRevision,
    );
    if (
      (payload.boardDisposition !== "ready" &&
        payload.boardDisposition !== "restart") ||
      (payload.boardDisposition === "ready" &&
        currentBoardDirtyRevision !== payload.coveredBoardDirtyRevision) ||
      (payload.boardDisposition === "restart" &&
        currentBoardDirtyRevision <= payload.coveredBoardDirtyRevision)
    ) {
      throw new TypeError("resync board disposition is invalid");
    }
    const readyPayload = Object.freeze({ ...payload });
    round.readyPayload = readyPayload;
    this.#state = "awaiting-ready-ack-send";
    this.#flushRequiredResyncControl();
  }

  #acceptComplete(payload: Readonly<Record<string, unknown>>): void {
    const keys = [
      "sessionId",
      "memberLegId",
      "memberEpoch",
      "resyncId",
      "boardSyncStep2MessageId",
      "boardSyncStep1MessageId",
      "pageSyncStep1MessageId",
      "pageSyncStep2MessageId",
      "coveredBoardDirtyRevision",
      "coveredPageDirtyGeneration",
      "boardDisposition",
      "currentBoardDirtyRevision",
      "pageDisposition",
      "currentPageDirtyGeneration",
      "completionDisposition",
      "completionBoardDirtyRevision",
      "completionPageDirtyGeneration",
    ] as const;
    requireExactKeys(payload, keys);
    this.#requireIdentity(payload);
    if (sameRecord(payload, this.#lastCompletePayload)) {
      return;
    }
    const round = this.#requireRound();
    if (
      this.#state !== "awaiting-complete" ||
      !recordHasPrefix(payload, this.#requireReadyAckPayload(round))
    ) {
      throw new TypeError("resync complete does not close the ready ACK");
    }
    const completionBoard = requireNonnegativeSafeInteger(
      payload.completionBoardDirtyRevision,
    );
    const completionPage = requireNonnegativeSafeInteger(
      payload.completionPageDirtyGeneration,
    );
    if (
      completionPage <
        requireNonnegativeSafeInteger(payload.currentPageDirtyGeneration) ||
      completionBoard <
        requireNonnegativeSafeInteger(payload.currentBoardDirtyRevision) ||
      (payload.completionDisposition !== "ready" &&
        payload.completionDisposition !== "restart")
    ) {
      throw new TypeError("resync complete high-water is invalid");
    }
    const wireReady =
      payload.boardDisposition === "ready" &&
      payload.pageDisposition === "ready" &&
      completionBoard === payload.coveredBoardDirtyRevision &&
      completionPage === payload.coveredPageDirtyGeneration;
    if (payload.completionDisposition !== (wireReady ? "ready" : "restart")) {
      throw new TypeError("resync complete disposition does not match its cut");
    }
    const locallyReady =
      wireReady &&
      this.#pageDirtyGeneration === payload.coveredPageDirtyGeneration;
    this.#lastResyncId = round.resyncId;
    this.#lastCompletePayload = Object.freeze({ ...payload });
    this.#pageClearedThrough = Math.max(
      this.#pageClearedThrough,
      requireNonnegativeSafeInteger(payload.coveredPageDirtyGeneration),
    );
    this.#round = undefined;
    if (locallyReady) {
      this.#awarenessOrdinaryGateOpened = true;
      this.#state = "ready";
      this.#setSynced(true);
      this.#publishLocalAwareness();
      return;
    }
    this.#state = "awaiting-request";
    this.#setSynced(false);
    this.#notifyDirtyGeneration();
  }

  #applyOrdinaryMessage(payload: Uint8Array, allowResponse: boolean): void {
    const decoder = decoding.createDecoder(payload);
    const messageType = decoding.readVarUint(decoder);
    if (messageType === MESSAGE_SYNC) {
      const response = this.#applySyncMessage(payload, allowResponse);
      if (allowResponse && response.byteLength > 1) {
        const result = this.#carrier.whiteboard.send(response);
        if (result.status === "paused") {
          this.#markDirty();
          this.#state = "awaiting-request";
          this.#setSynced(false);
        } else if (result.status !== "accepted")
          this.#handleSendFailure(result);
      }
      return;
    }
    const update = decoding.readVarUint8Array(decoder);
    awarenessProtocol.applyAwarenessUpdate(this.awareness, update, this);
  }

  #applySyncMessage(payload: Uint8Array, requireResponse: boolean): Uint8Array {
    const decoder = decoding.createDecoder(payload);
    if (decoding.readVarUint(decoder) !== MESSAGE_SYNC) {
      throw new TypeError("expected whiteboard sync bytes");
    }
    const encoder = encoding.createEncoder();
    encoding.writeVarUint(encoder, MESSAGE_SYNC);
    syncProtocol.readSyncMessage(decoder, encoder, this.#doc, this);
    const response = encoding.toUint8Array(encoder);
    if (requireResponse && response.byteLength <= 1) {
      throw new TypeError("SyncStep1 did not produce SyncStep2");
    }
    return response;
  }

  #flushRequiredResyncWhiteboard(): void {
    const round = this.#requireRound();
    const step =
      this.#state === "awaiting-page-step-1-send"
        ? 1
        : this.#state === "awaiting-page-step-2-send"
          ? 2
          : undefined;
    if (step === undefined || !this.#carrier.whiteboard.writable) return;
    const payload =
      step === 1 ? round.pendingPageSyncStep1 : round.pendingPageSyncStep2;
    if (payload === undefined) {
      throw new TypeError("required resync whiteboard payload is absent");
    }
    validateW10YProtocolPayload(payload);
    const result = this.#carrier.whiteboard.send(payload);
    if (result.status === "paused") return;
    if (result.status !== "accepted") return this.#handleSendFailure(result);
    const messageId = this.#carrier.whiteboard.lastOutboundMessageId;
    requirePositiveUint32(messageId);
    if (step === 1) {
      round.pendingPageSyncStep1 = undefined;
      round.pageSyncStep1MessageId = messageId;
      this.#state = "awaiting-board-step-2";
    } else {
      round.pendingPageSyncStep2 = undefined;
      round.pageSyncStep2MessageId = messageId;
      this.#state = "awaiting-ready";
    }
  }

  #flushRequiredResyncControl(): void {
    const round = this.#requireRound();
    if (this.#state === "awaiting-start-send") {
      const pageDirtyGeneration = this.#pageDirtyGeneration;
      const payload = Object.freeze({
        sessionId: this.#identity.sessionId,
        memberLegId: this.#identity.memberLegId,
        memberEpoch: this.#identity.memberEpoch,
        resyncId: round.resyncId,
        pageDirtyGeneration,
      });
      const result = this.#carrier.control.send(
        "whiteboard-resync-start",
        payload,
      );
      if (result.status === "paused") return;
      if (result.status !== "accepted") return this.#handleSendFailure(result);
      round.pageDirtyGeneration = pageDirtyGeneration;
      round.startPayload = payload;
      this.#state = "awaiting-start-ack";
      return;
    }
    if (this.#state !== "awaiting-ready-ack-send") return;
    const readyPayload = round.readyPayload;
    if (readyPayload === undefined) {
      throw new TypeError("resync ready payload is absent");
    }
    const currentPageDirtyGeneration = this.#pageDirtyGeneration;
    const readyAckPayload = Object.freeze({
      ...readyPayload,
      pageDisposition:
        readyPayload.boardDisposition === "ready" &&
        currentPageDirtyGeneration === round.pageDirtyGeneration
          ? "ready"
          : "restart",
      currentPageDirtyGeneration,
    });
    const result = this.#carrier.control.send(
      "whiteboard-resync-ready-ack",
      readyAckPayload,
    );
    if (result.status === "paused") return;
    if (result.status !== "accepted") return this.#handleSendFailure(result);
    round.readyAckPayload = readyAckPayload;
    this.#state = "awaiting-complete";
  }

  #markDirty(): void {
    if (this.#pageDirtyGeneration >= Number.MAX_SAFE_INTEGER) {
      this.#recoverableFailure({
        class: "local",
        cause: "counter-exhausted",
      });
      return;
    }
    this.#pageDirtyGeneration += 1;
    if (this.#state === "ready") {
      this.#state = "awaiting-request";
      this.#setSynced(false);
    }
    this.#notifyDirtyGeneration();
  }

  #notifyDirtyGeneration(): void {
    if (
      !this.#connected ||
      this.#pageDirtyGeneration <= this.#pageClearedThrough ||
      this.#pageDirtyGeneration <= this.#lastNotifiedGeneration
    ) {
      return;
    }
    const generation = this.#pageDirtyGeneration;
    if (
      this.#carrier.control.send("whiteboard-resync-needed", {
        sessionId: this.#identity.sessionId,
        memberLegId: this.#identity.memberLegId,
        memberEpoch: this.#identity.memberEpoch,
        pageDirtyGeneration: generation,
      }).status === "accepted"
    ) {
      this.#lastNotifiedGeneration = generation;
    }
  }

  #publishLocalAwareness(): void {
    if (!this.#ensureCurrentAwarenessId()) return;
    this.#coalesceLocalAwareness();
    this.#flushPendingAwareness();
  }

  #coalesceLocalAwareness(): void {
    const clientId = this.#awareness.clientID;
    const meta = this.#awareness.meta.get(clientId);
    if (meta === undefined) return;
    const tombstone = this.#awareness.getLocalState() === null;
    if (
      this.#suppressedAwareness?.clientId === clientId &&
      this.#suppressedAwareness.clock === meta.clock
    ) {
      return;
    }
    if (!tombstone && meta.clock >= Number.MAX_SAFE_INTEGER) {
      this.#retireAwareness("awareness-clock-exhausted");
      return;
    }
    const next = Object.freeze({ clientId, clock: meta.clock, tombstone });
    const current = this.#pendingAwareness.get(clientId);
    if (
      current !== undefined &&
      (current.clock > next.clock ||
        (current.clock === next.clock &&
          (current.tombstone || !next.tombstone)))
    ) {
      return;
    }
    if (
      !this.#pendingAwareness.has(clientId) &&
      this.#pendingAwareness.size >= 11
    ) {
      this.#retireAwareness("control-queue-overflow");
      return;
    }
    this.#pendingAwareness.set(clientId, next);
  }

  #pendingAwarenessPrecedesNextRound(): boolean {
    return this.#awarenessOrdinaryGateOpened && this.#pendingAwareness.size > 0;
  }

  #flushPendingAwareness(): void {
    if (
      !this.#connected ||
      (this.#state !== "ready" &&
        (this.#state !== "awaiting-request-cut" ||
          !this.#awarenessOrdinaryGateOpened)) ||
      this.#awarenessSendInProgress
    ) {
      return;
    }
    let candidate: W10PendingAwareness | undefined;
    let currentState: Record<string, unknown> | undefined;
    while (candidate === undefined) {
      candidate = [...this.#pendingAwareness.values()].sort(
        (left, right) =>
          Number(right.tombstone) - Number(left.tombstone) ||
          left.clientId - right.clientId,
      )[0];
      if (candidate === undefined) return;
      const currentMeta = this.#awareness.meta.get(candidate.clientId);
      currentState = this.#awareness.getStates().get(candidate.clientId);
      if (
        currentMeta !== undefined &&
        currentMeta.clock === candidate.clock &&
        (currentState === undefined) === candidate.tombstone
      ) {
        break;
      }
      this.#pendingAwareness.delete(candidate.clientId);
      this.#coalesceLocalAwareness();
      candidate = undefined;
    }
    if (currentState !== undefined) {
      const stateBytes = new TextEncoder().encode(
        JSON.stringify(currentState),
      ).byteLength;
      if (stateBytes > PROTOCOL_CONSTANTS.WB_AWARENESS_MAX_BYTES) {
        this.#pendingAwareness.delete(candidate.clientId);
        return;
      }
    }
    const encoder = encoding.createEncoder();
    encoding.writeVarUint(encoder, MESSAGE_AWARENESS);
    encoding.writeVarUint8Array(
      encoder,
      awarenessProtocol.encodeAwarenessUpdate(this.#awareness, [
        candidate.clientId,
      ]),
    );
    const message = encoding.toUint8Array(encoder);
    try {
      validateW10YProtocolPayload(message);
    } catch (cause) {
      this.#hardFailure(cause);
      return;
    }
    this.#awarenessSendInProgress = true;
    let result: W10PageSendResult;
    try {
      result = this.#carrier.whiteboard.send(message);
    } finally {
      this.#awarenessSendInProgress = false;
    }
    if (result.status === "accepted") {
      const sourceWhiteboardMessageId =
        this.#carrier.whiteboard.lastOutboundMessageId;
      requirePositiveUint32(sourceWhiteboardMessageId);
      this.#awarenessClaimMayExist = true;
      this.#awarenessSubmissionCount += 1;
      this.#lastAwarenessDisposition = Object.freeze({
        sourceWhiteboardMessageId,
        clientId: candidate.clientId,
        sourceClock: candidate.clock,
      });
      this.#highestAwarenessSourceMessageId = sourceWhiteboardMessageId;
      if (
        this.#pendingAwareness.get(candidate.clientId)?.clock ===
        candidate.clock
      ) {
        this.#pendingAwareness.delete(candidate.clientId);
      }
      return;
    }
    if (result.status !== "paused") {
      this.#handleSendFailure(result);
    }
  }

  #acceptAwarenessRejection(payload: Readonly<Record<string, unknown>>): void {
    requireExactKeys(payload, [
      "sessionId",
      "memberLegId",
      "memberEpoch",
      "sourceWhiteboardMessageId",
      "clientId",
      "sourceClock",
      "reason",
    ]);
    this.#requireIdentity(payload);
    const sourceWhiteboardMessageId = requirePositiveUint32(
      payload.sourceWhiteboardMessageId,
    );
    const clientId = requireUint32(payload.clientId);
    const sourceClock = requireNonnegativeSafeInteger(payload.sourceClock);
    const reason = requireAwarenessRejectionReason(payload.reason);
    const signature = JSON.stringify(payload);
    const lastApplied = this.#lastAppliedAwarenessRejection;
    if (
      lastApplied?.disposition.sourceWhiteboardMessageId ===
      sourceWhiteboardMessageId
    ) {
      if (signature === lastApplied.signature) return;
      this.#controlProtocolFailure(
        new TypeError("awareness rejection retry changed its payload"),
      );
      return;
    }
    const current = this.#lastAwarenessDisposition;
    if (current === undefined) {
      if (sourceWhiteboardMessageId <= this.#highestAwarenessSourceMessageId) {
        return;
      }
      this.#controlProtocolFailure(
        new TypeError("awareness rejection names an unissued candidate"),
      );
      return;
    }
    if (sourceWhiteboardMessageId < current.sourceWhiteboardMessageId) return;
    if (
      sourceWhiteboardMessageId > current.sourceWhiteboardMessageId ||
      clientId !== current.clientId ||
      sourceClock !== current.sourceClock
    ) {
      this.#controlProtocolFailure(
        new TypeError("awareness rejection tuple does not correlate"),
      );
      return;
    }
    const localMeta = this.#awareness.meta.get(this.#awareness.clientID);
    if (
      this.#awareness.clientID !== clientId ||
      (localMeta !== undefined && localMeta.clock > sourceClock)
    ) {
      return;
    }
    this.#lastAppliedAwarenessRejection = Object.freeze({
      signature,
      disposition: Object.freeze({
        sourceWhiteboardMessageId,
        clientId,
        sourceClock,
      }),
    });
    if (
      reason === "AWARENESS_CLIENT_COLLISION" ||
      reason === "AWARENESS_CLOCK_EXHAUSTED"
    ) {
      if (
        this.#awarenessSubmissionCount === 1 &&
        this.#pendingAwareness.size === 0
      ) {
        this.#awarenessClaimMayExist = false;
        this.#rebuildAwareness();
        return;
      }
      this.#retireAwareness(
        reason === "AWARENESS_CLIENT_COLLISION"
          ? "awareness-client-collision"
          : "awareness-clock-exhausted",
      );
      return;
    }
    this.#pendingAwareness.delete(clientId);
    this.#suppressedAwareness = Object.freeze({
      clientId,
      clock: sourceClock,
    });
  }

  #ensureCurrentAwarenessId(): boolean {
    if (this.#awareness.clientID === this.#doc.clientID) return true;
    if (!this.#awarenessClaimMayExist && this.#awarenessSubmissionCount === 0) {
      this.#rebuildAwareness();
    } else {
      this.#retireAwareness("awareness-client-id-changed");
    }
    return false;
  }

  #rebuildAwareness(): void {
    if (this.#pageDirtyGeneration >= Number.MAX_SAFE_INTEGER) {
      this.#retireAwareness("counter-exhausted");
      return;
    }
    const previous = this.#awareness;
    const previousClientId = previous.clientID;
    const localState = previous.getLocalState();
    this.#awarenessOrdinaryGateOpened = false;
    previous.off("update", this.#onAwarenessUpdate);
    previous.destroy();
    let nextClientId = randomUint32();
    while (nextClientId === previousClientId) nextClientId = randomUint32();
    this.#doc.clientID = nextClientId;
    const next = new awarenessProtocol.Awareness(this.#doc);
    if (localState !== null) next.setLocalState(localState);
    this.#awareness = next;
    this.#pendingAwareness.clear();
    this.#lastAwarenessDisposition = undefined;
    this.#awarenessSubmissionCount = 0;
    this.#awarenessClaimMayExist = false;
    this.#suppressedAwareness = undefined;
    this.#markDirty();
    next.on("update", this.#onAwarenessUpdate);
    this.#onAwarenessRebuilt?.(previous, next);
  }

  #retireAwareness(cause: W10PageLocalCause): void {
    this.#pendingAwareness.clear();
    this.#recoverableFailure({ class: "local", cause });
  }

  #requireRound(): W10ResyncRound {
    if (this.#round === undefined)
      throw new TypeError("resync round is absent");
    return this.#round;
  }

  #requireReadyAckPayload(
    round: W10ResyncRound,
  ): Readonly<Record<string, unknown>> {
    if (round.readyAckPayload === undefined) {
      throw new TypeError("resync ready ACK is absent");
    }
    return round.readyAckPayload;
  }

  #requireIdentity(payload: Readonly<Record<string, unknown>>): void {
    if (
      payload.sessionId !== this.#identity.sessionId ||
      payload.memberLegId !== this.#identity.memberLegId ||
      payload.memberEpoch !== this.#identity.memberEpoch
    ) {
      throw new TypeError("resync identity does not match the current carrier");
    }
  }

  #setConnected(connected: boolean, removeRemoteAwareness = false): void {
    const changed = this.#connected !== connected;
    this.#connected = connected;
    if (!connected) {
      this.#setSynced(false);
      if (removeRemoteAwareness) this.#removeRemoteAwareness();
    }
    if (!changed) return;
    const status = connected ? "connected" : "disconnected";
    for (const listener of this.#statusListeners) listener({ status });
  }

  #setSynced(synced: boolean): void {
    if (this.#synced === synced) return;
    this.#synced = synced;
    for (const listener of this.#syncedListeners) listener(synced);
  }

  #removeRemoteAwareness(): void {
    const remoteClients = [...this.awareness.getStates().keys()].filter(
      (clientId) => clientId !== this.#awareness.clientID,
    );
    if (remoteClients.length > 0) {
      awarenessProtocol.removeAwarenessStates(
        this.awareness,
        remoteClients,
        this,
      );
    }
  }

  #handleSendFailure(result: W10PageSendResult): void {
    if (result.status === "accepted") return;
    if (result.status === "paused") return;
    if (result.status === "local-failed") {
      this.#recoverableFailure({ class: "local", cause: result.cause });
      return;
    }
    if (result.status === "operational-failed") {
      this.#recoverableFailure({
        class: "operational",
        cause: result.reason,
      });
      return;
    }
    this.#hardFailure(new TypeError(result.reason));
  }

  #recoverableFailure(failure: W10WhiteboardRecoverableFailure): void {
    if (!this.#shouldConnect) return;
    this.disconnect();
    this.#onRecoverableFailure(failure);
  }

  #hardFailure(cause: unknown): void {
    if (!this.#shouldConnect) return;
    this.disconnect();
    this.#onProtocolError(new Error("Y_PROTOCOL_INTEGRITY", { cause }));
  }

  #controlProtocolFailure(cause: unknown): void {
    if (!this.#shouldConnect) return;
    this.disconnect();
    this.#onControlProtocolError(
      new Error("CONTROL_PROTOCOL_INTEGRITY", { cause }),
    );
  }
}

function syncSubtype(payload: Uint8Array): number {
  const decoder = decoding.createDecoder(payload);
  if (decoding.readVarUint(decoder) !== MESSAGE_SYNC) {
    throw new TypeError("resync message is not a sync message");
  }
  return decoding.readVarUint(decoder);
}

function requireExactKeys(
  value: Readonly<Record<string, unknown>>,
  expected: readonly string[],
): void {
  const actual = Object.keys(value);
  if (
    actual.length !== expected.length ||
    expected.some((key, index) => actual[index] !== key)
  ) {
    throw new TypeError("resync payload key order is invalid");
  }
}

function requirePositiveSafeInteger(value: unknown): number {
  if (!Number.isSafeInteger(value) || (value as number) <= 0) {
    throw new TypeError("resync value must be a positive safe integer");
  }
  return value as number;
}

function requireNonnegativeSafeInteger(value: unknown): number {
  if (!Number.isSafeInteger(value) || (value as number) < 0) {
    throw new TypeError("resync value must be a nonnegative safe integer");
  }
  return value as number;
}

function requireUint32(value: unknown): number {
  if (
    !Number.isInteger(value) ||
    (value as number) < 0 ||
    (value as number) > 0xffff_ffff
  ) {
    throw new TypeError("resync value must be uint32");
  }
  return value as number;
}

function requirePositiveUint32(value: unknown): number {
  const result = requireUint32(value);
  if (result === 0) throw new TypeError("resync outer id must be positive");
  return result;
}

function requireAwarenessRejectionReason(
  value: unknown,
): W10AwarenessRejectionReason {
  if (
    value !== "AWARENESS_CLIENT_MISMATCH" &&
    value !== "AWARENESS_CLIENT_COLLISION" &&
    value !== "AWARENESS_CLIENT_LIMIT" &&
    value !== "AWARENESS_BYTE_LIMIT" &&
    value !== "AWARENESS_CLOCK_EXHAUSTED"
  ) {
    throw new TypeError("awareness rejection reason is invalid");
  }
  return value;
}

function sameRecord(
  left: Readonly<Record<string, unknown>>,
  right: Readonly<Record<string, unknown>> | undefined,
): boolean {
  return right !== undefined && JSON.stringify(left) === JSON.stringify(right);
}

function recordHasPrefix(
  value: Readonly<Record<string, unknown>>,
  prefix: Readonly<Record<string, unknown>>,
): boolean {
  return Object.entries(prefix).every(
    ([key, expected]) => value[key] === expected,
  );
}

export function validateW10YProtocolPayload(message: Uint8Array): void {
  if (!(message instanceof Uint8Array) || message.byteLength === 0) {
    throw new TypeError(
      "W10 y-protocols payload must be a non-empty Uint8Array",
    );
  }
  const decoder = decoding.createDecoder(message);
  const messageType = decoding.readVarUint(decoder);
  if (messageType === MESSAGE_SYNC) {
    const subtype = decoding.readVarUint(decoder);
    if (subtype !== 0 && subtype !== 1 && subtype !== 2) {
      throw new TypeError("W10 y-protocols sync subtype is unknown");
    }
    const body = decoding.readVarUint8Array(decoder);
    if (decoding.hasContent(decoder)) {
      throw new TypeError("W10 y-protocols sync payload has trailing bytes");
    }
    if (subtype === 0) {
      validateStateVector(body);
      const doc = new Y.Doc({ gc: true });
      try {
        Y.encodeStateAsUpdate(doc, body);
      } finally {
        doc.destroy();
      }
      return;
    }
    const bodyDecoder = decoding.createDecoder(body);
    const doc = new Y.Doc({ gc: true });
    try {
      Y.readUpdate(bodyDecoder, doc, "w10-validation");
      if (decoding.hasContent(bodyDecoder)) {
        throw new TypeError("W10 Yjs update body has trailing bytes");
      }
    } finally {
      doc.destroy();
    }
    return;
  }
  if (messageType === MESSAGE_AWARENESS) {
    const body = decoding.readVarUint8Array(decoder);
    if (decoding.hasContent(decoder)) {
      throw new TypeError(
        "W10 y-protocols awareness payload has trailing bytes",
      );
    }
    validateAwareness(body);
    const doc = new Y.Doc({ gc: true });
    const awareness = new awarenessProtocol.Awareness(doc);
    try {
      awarenessProtocol.applyAwarenessUpdate(awareness, body, "w10-validation");
    } finally {
      awareness.destroy();
      doc.destroy();
    }
    return;
  }
  throw new TypeError("W10 y-protocols outer message type is unknown");
}

function validateStateVector(body: Uint8Array): void {
  const decoder = decoding.createDecoder(body);
  const count = decoding.readVarUint(decoder);
  if (count > Math.floor((body.byteLength - decoder.pos) / 2)) {
    throw new TypeError("W10 Yjs state-vector entry count exceeds its body");
  }
  for (let index = 0; index < count; index += 1) {
    decoding.readVarUint(decoder);
    decoding.readVarUint(decoder);
  }
  if (decoding.hasContent(decoder)) {
    throw new TypeError("W10 Yjs state vector has trailing bytes");
  }
}

function validateAwareness(body: Uint8Array): void {
  const decoder = decoding.createDecoder(body);
  const count = decoding.readVarUint(decoder);
  if (count > Math.floor((body.byteLength - decoder.pos) / 3)) {
    throw new TypeError("W10 awareness entry count exceeds its body");
  }
  for (let index = 0; index < count; index += 1) {
    decoding.readVarUint(decoder);
    decoding.readVarUint(decoder);
    JSON.parse(decoding.readVarString(decoder));
  }
  if (decoding.hasContent(decoder)) {
    throw new TypeError("W10 awareness payload has trailing bytes");
  }
}

interface WhiteboardConnectionCommonOptions {
  readonly discovery: HostDiscovery;
  readonly clientId: string;
  readonly sessionId: string;
  readonly displayName: string;
  readonly colorSeed?: number;
  readonly monotonicNow?: () => number;
  readonly providerFactory?: WhiteboardProviderFactory;
  readonly dataChannelProviderFactory?: WhiteboardDataChannelProviderFactory;
  readonly persistenceFactory?: WhiteboardPersistenceFactory;
  readonly onPrimaryRecoverableFailure?: (
    failure: W10WhiteboardRecoverableFailure,
  ) => void;
  readonly onPrimaryProtocolFailure?: (
    reason: W10PageHardCause | "Y_PROTOCOL_INTEGRITY",
  ) => void;
}

export type WhiteboardConnectionOptions = WhiteboardConnectionCommonOptions &
  (
    | {
        readonly yjsTicket: string;
        readonly primaryCarrier?: never;
        readonly memberLegId?: never;
        readonly memberEpoch?: never;
      }
    | {
        readonly primaryCarrier: W10MemberPrimaryCarrier;
        readonly memberLegId: string;
        readonly memberEpoch: number;
        readonly yjsTicket?: never;
      }
  );

export interface WhiteboardProviderLike {
  readonly awareness: WebsocketProvider["awareness"];
  readonly ws?: WebSocket | null;
  readonly wsconnected: boolean;
  readonly readyState?: number;
  readonly synced?: boolean;
  connect(): void;
  disconnect(): void;
  destroy(): void;
  on(event: "status", listener: (event: { status: string }) => void): void;
  off(event: "status", listener: (event: { status: string }) => void): void;
  onSynced(listener: (synced: boolean) => void): void;
  offSynced(listener: (synced: boolean) => void): void;
}

export type WhiteboardProviderFactory = (
  target: WhiteboardProviderTarget,
  doc: Y.Doc,
  awareness: awarenessProtocol.Awareness,
) => WhiteboardProviderLike;

export type WhiteboardDataChannelProviderFactory = (
  carrier: W10MemberPrimaryCarrier,
  identity: W10MemberCarrierIdentity,
  doc: Y.Doc,
  onRecoverableFailure: (failure: W10WhiteboardRecoverableFailure) => void,
  onProtocolFailure: (
    reason: W10PageHardCause | "Y_PROTOCOL_INTEGRITY",
  ) => void,
  awareness: awarenessProtocol.Awareness,
  onAwarenessRebuilt: (
    previous: awarenessProtocol.Awareness,
    next: awarenessProtocol.Awareness,
  ) => void,
) => WhiteboardProviderLike;

export type WhiteboardMemberCarrier =
  | "pc-primary"
  | "fallback-pending"
  | "wss-fallback"
  | "unavailable";
export type WhiteboardAvailability = "ready" | "resyncing" | "unavailable";

export interface WhiteboardPersistenceLike {
  readonly whenSynced: Promise<unknown>;
  clearData(): Promise<unknown>;
  destroy(): Promise<unknown>;
}

export type WhiteboardPersistenceFactory = (
  name: string,
  doc: Y.Doc,
) => WhiteboardPersistenceLike;

export interface WhiteboardProviderTarget {
  readonly serverUrl: string;
  readonly roomName: string;
  readonly params: Readonly<Record<string, string>>;
}

type Listener = (snapshot: WhiteboardSessionSnapshot) => void;

export class WhiteboardSession {
  readonly sessionId: string;
  readonly doc: Y.Doc;
  readonly model: WhiteboardDocument;
  #awareness: awarenessProtocol.Awareness;
  readonly #discovery: HostDiscovery;
  readonly #clientId: string;
  readonly #displayName: string;
  readonly #colorSeed: number;
  readonly #providerFactory: WhiteboardProviderFactory;
  readonly #dataChannelProviderFactory: WhiteboardDataChannelProviderFactory;
  readonly #onPrimaryRecoverableFailure:
    | ((failure: W10WhiteboardRecoverableFailure) => void)
    | undefined;
  readonly #onPrimaryProtocolFailure:
    | ((reason: W10PageHardCause | "Y_PROTOCOL_INTEGRITY") => void)
    | undefined;
  readonly #primaryCarrier: W10MemberPrimaryCarrier | undefined;
  readonly #persistence: WhiteboardPersistenceLike;
  readonly #hostClock: HostRelativeClock;
  readonly #listeners = new Set<Listener>();
  #provider: WhiteboardProviderLike;
  #awarenessController: WhiteboardAwarenessController;
  #persistenceSynced = false;
  #destroyed = false;
  #memberCarrier: WhiteboardMemberCarrier;
  #unobservePages: () => void;
  #freshAwarenessOnNextCarrier = false;

  public constructor(options: WhiteboardConnectionOptions) {
    this.sessionId = options.sessionId;
    this.#discovery = options.discovery;
    this.#clientId = options.clientId;
    this.#displayName = options.displayName;
    this.#colorSeed = options.colorSeed ?? stableColorSeed(options.clientId);
    this.#providerFactory = options.providerFactory ?? createProvider;
    this.#dataChannelProviderFactory =
      options.dataChannelProviderFactory ?? createDataChannelProvider;
    this.#onPrimaryRecoverableFailure = options.onPrimaryRecoverableFailure;
    this.#onPrimaryProtocolFailure = options.onPrimaryProtocolFailure;
    this.#primaryCarrier = options.primaryCarrier;
    this.#hostClock = new HostRelativeClock(
      options.monotonicNow ?? (() => performance.now()),
    );
    this.doc = new Y.Doc({ gc: true });
    this.#awareness = new awarenessProtocol.Awareness(this.doc);
    this.model = new WhiteboardDocument(this.doc, {
      clientId: options.clientId,
    });
    const persistenceFactory =
      options.persistenceFactory ??
      ((name, doc) => new IndexeddbPersistence(name, doc));
    this.#persistence = persistenceFactory(
      persistenceName(options.sessionId),
      this.doc,
    );
    if (options.primaryCarrier === undefined) {
      this.#memberCarrier = "wss-fallback";
      this.#provider = this.#createProvider(options.yjsTicket);
    } else {
      this.#memberCarrier = "pc-primary";
      this.#provider = this.#dataChannelProviderFactory(
        options.primaryCarrier,
        {
          sessionId: options.sessionId,
          memberLegId: options.memberLegId,
          memberEpoch: options.memberEpoch,
        },
        this.doc,
        this.#handlePrimaryRecoverableFailure,
        this.#handlePrimaryProtocolFailure,
        this.#awareness,
        this.#handleAwarenessRebuilt,
      );
    }
    if (this.#provider.awareness !== this.#awareness) {
      this.#provider.destroy();
      this.#awareness.destroy();
      throw new TypeError("whiteboard provider replaced the session Awareness");
    }
    this.#awarenessController = this.#createAwarenessController();
    this.#bindProvider();
    this.#unobservePages = this.model.observeDocument(() => this.#emit());
    void this.#persistence.whenSynced
      .then(() => {
        if (this.#destroyed) return;
        this.#persistenceSynced = true;
        this.#emit();
      })
      .catch(() => undefined);
  }

  public get snapshot(): WhiteboardSessionSnapshot {
    const presentation = hostPresentationState(this.#provider.awareness);
    if (presentation.hostTime !== undefined)
      this.#hostClock.anchor(presentation.hostTime);
    const readyState =
      this.#provider.readyState ?? this.#provider.ws?.readyState;
    return {
      ...(readyState === undefined ? {} : { readyState }),
      persistenceSynced: this.#persistenceSynced,
      activePage: presentation.activePage,
      boardLocked: presentation.boardLocked,
      hostTimeAnchored: this.#hostClock.anchored,
      canUndo: this.model.canUndo,
      canRedo: this.model.canRedo,
    };
  }

  public get memberCarrier(): WhiteboardMemberCarrier {
    return this.#memberCarrier;
  }

  public get whiteboardAvailability(): WhiteboardAvailability {
    if (this.#memberCarrier === "unavailable") return "unavailable";
    if (
      this.#memberCarrier === "fallback-pending" ||
      !this.#provider.wsconnected ||
      this.#provider.synced === false
    ) {
      return "resyncing";
    }
    return "ready";
  }

  public subscribe(listener: Listener): () => void {
    this.#listeners.add(listener);
    listener(this.snapshot);
    return () => this.#listeners.delete(listener);
  }

  public createdAt(): number {
    return this.#hostClock.createdAt();
  }

  public reconnect(yjsTicket: string): void {
    this.fallbackToWebSocket(yjsTicket);
  }

  /** Replace only the carrier; the Y.Doc and IndexedDB replica stay alive. */
  public fallbackToWebSocket(yjsTicket: string): void {
    if (this.#destroyed || this.#memberCarrier === "unavailable") return;
    this.#memberCarrier = "fallback-pending";
    this.#primaryCarrier?.close();
    const awarenessClientId = this.#awareness.clientID;
    const localState = this.#awareness.getLocalState();
    const localClock = this.#awareness.meta.get(awarenessClientId)?.clock ?? 0;
    if (awarenessClientId !== this.doc.clientID) {
      this.#freshAwarenessOnNextCarrier = true;
    }
    if (
      localState !== null &&
      localClock > Number.MAX_SAFE_INTEGER - 3 &&
      !this.#freshAwarenessOnNextCarrier
    ) {
      this.#freshAwarenessOnNextCarrier = true;
      this.#onPrimaryRecoverableFailure?.({
        class: "local",
        cause: "awareness-clock-exhausted",
      });
    }
    if (this.#freshAwarenessOnNextCarrier) {
      this.#unbindProvider();
      this.#provider.destroy();
      this.#rebuildOwnedAwareness();
      this.#freshAwarenessOnNextCarrier = false;
      const provider = this.#createProvider(yjsTicket);
      if (provider.awareness !== this.#awareness) {
        provider.destroy();
        throw new TypeError(
          "whiteboard provider replaced the session Awareness",
        );
      }
      this.#provider = provider;
      this.#bindProvider();
      this.#emit();
      return;
    }
    const provider = this.#createProvider(yjsTicket);
    this.#replaceProvider(provider);
    this.#emit();
  }

  public retirePrimaryCarrier(failure: W10PageCarrierFailure): void {
    if (failure.class === "hard") {
      this.#handlePrimaryProtocolFailure(failure.cause);
      return;
    }
    if (failure.class === "policy") {
      this.#handlePrimaryPolicyFailure();
      return;
    }
    this.#handlePrimaryRecoverableFailure(
      failure.class === "local"
        ? { class: "local", cause: failure.cause }
        : failure.cause === "WHITEBOARD_MESSAGE_TOO_LARGE"
          ? { class: "operational", cause: failure.cause }
          : { class: "local", cause: "channel-closed" },
    );
  }

  public undo(): boolean {
    const changed = this.model.undo();
    if (changed) this.#emit();
    return changed;
  }

  public redo(): boolean {
    const changed = this.model.redo();
    if (changed) this.#emit();
    return changed;
  }

  public updateCursor(
    cursor:
      | { readonly page: string; readonly x: number; readonly y: number }
      | undefined,
  ): void {
    this.#awarenessController.updateCursor(cursor);
  }

  public updatePreview(preview: WhiteboardPreview | undefined): void {
    this.#awarenessController.updatePreview(preview);
  }

  public remoteAwareness(): ReadonlyArray<WhiteboardAwarenessState> {
    return this.remoteAwarenessWithIdentity().map(([, state]) => state);
  }

  /** Presentation-only identity; wire awareness and the legacy state API stay unchanged. */
  public remoteAwarenessWithIdentity(): ReadonlyArray<WhiteboardCanvasRemoteState> {
    return remoteAwarenessStates(this.#provider.awareness);
  }

  public async destroy(clearPersistence: boolean): Promise<void> {
    if (this.#destroyed) return;
    this.#destroyed = true;
    this.#unbindProvider();
    this.#awarenessController.destroy();
    this.#provider.destroy();
    this.#awareness.destroy();
    this.#primaryCarrier?.close();
    this.#unobservePages();
    this.model.destroy();
    if (clearPersistence) await this.#persistence.clearData();
    else await this.#persistence.destroy();
    this.doc.destroy();
    this.#listeners.clear();
  }

  #createProvider(yjsTicket: string): WhiteboardProviderLike {
    return this.#providerFactory(
      providerTarget(this.#discovery.yjsUrl, {
        clientId: this.#clientId,
        sessionId: this.sessionId,
        yjsTicket,
      }),
      this.doc,
      this.#awareness,
    );
  }

  #createAwarenessController(): WhiteboardAwarenessController {
    return new WhiteboardAwarenessController(this.#awareness, {
      name: this.#displayName,
      colorSeed: this.#colorSeed,
    });
  }

  #replaceProvider(provider: WhiteboardProviderLike): void {
    if (provider.awareness !== this.#awareness) {
      provider.destroy();
      throw new TypeError("whiteboard provider replaced the session Awareness");
    }
    this.#unbindProvider();
    this.#provider.destroy();
    const localState = this.#awareness.getLocalState();
    if (localState !== null) {
      this.#awareness.setLocalState(null);
      this.#awareness.setLocalState(localState);
    }
    this.#provider = provider;
    this.#bindProvider();
  }

  readonly #handlePrimaryRecoverableFailure = (
    failure: W10WhiteboardRecoverableFailure,
  ): void => {
    if (this.#destroyed || this.#memberCarrier !== "pc-primary") return;
    if (
      failure.class === "local" &&
      (failure.cause === "awareness-client-id-changed" ||
        failure.cause === "awareness-client-collision" ||
        failure.cause === "awareness-clock-exhausted")
    ) {
      this.#freshAwarenessOnNextCarrier = true;
    }
    this.#memberCarrier = "fallback-pending";
    this.#provider.disconnect();
    if (this.#primaryCarrier?.retireForFallback !== undefined) {
      this.#primaryCarrier.retireForFallback();
    } else {
      this.#primaryCarrier?.close();
    }
    this.#onPrimaryRecoverableFailure?.(failure);
    this.#emit();
  };

  #handlePrimaryPolicyFailure(): void {
    if (
      this.#destroyed ||
      (this.#memberCarrier !== "pc-primary" &&
        this.#memberCarrier !== "fallback-pending")
    ) {
      return;
    }
    this.#memberCarrier = "unavailable";
    this.#provider.disconnect();
    if (this.#primaryCarrier?.retireForFallback !== undefined) {
      this.#primaryCarrier.retireForFallback();
    } else {
      this.#primaryCarrier?.close();
    }
    this.#emit();
  }

  readonly #handlePrimaryProtocolFailure = (
    reason: W10PageHardCause | "Y_PROTOCOL_INTEGRITY",
  ): void => {
    if (
      this.#destroyed ||
      (this.#memberCarrier !== "pc-primary" &&
        this.#memberCarrier !== "fallback-pending")
    ) {
      return;
    }
    this.#memberCarrier = "unavailable";
    this.#provider.disconnect();
    this.#primaryCarrier?.close();
    this.#onPrimaryProtocolFailure?.(reason);
    this.#emit();
  };

  readonly #handleAwarenessRebuilt = (
    previous: awarenessProtocol.Awareness,
    next: awarenessProtocol.Awareness,
  ): void => {
    if (previous !== this.#awareness || this.#provider.awareness !== next) {
      this.#handlePrimaryProtocolFailure("CONTROL_PROTOCOL_INTEGRITY");
      return;
    }
    previous.off("change", this.#onAwarenessChange);
    this.#awarenessController.destroy();
    this.#awareness = next;
    this.#awarenessController = this.#createAwarenessController();
    next.on("change", this.#onAwarenessChange);
  };

  readonly #onProviderStatus = (): void => this.#emit();
  readonly #onProviderSynced = (synced: boolean): void => {
    if (synced && this.#memberCarrier === "fallback-pending") {
      this.#memberCarrier = "wss-fallback";
    }
    this.#emit();
  };
  readonly #onAwarenessChange = (): void => this.#emit();

  #rebuildOwnedAwareness(): void {
    const previous = this.#awareness;
    const previousClientId = previous.clientID;
    const localState = previous.getLocalState();
    previous.off("change", this.#onAwarenessChange);
    this.#awarenessController.destroy();
    previous.destroy();
    let nextClientId = randomUint32();
    while (nextClientId === previousClientId) nextClientId = randomUint32();
    this.doc.clientID = nextClientId;
    const next = new awarenessProtocol.Awareness(this.doc);
    if (localState !== null) next.setLocalState(localState);
    this.#awareness = next;
    this.#awarenessController = this.#createAwarenessController();
  }

  #bindProvider(): void {
    this.#provider.on("status", this.#onProviderStatus);
    this.#provider.onSynced(this.#onProviderSynced);
    this.#provider.awareness.on("change", this.#onAwarenessChange);
    this.#provider.connect();
    if (this.#provider.synced === true) this.#onProviderSynced(true);
  }

  #unbindProvider(): void {
    this.#provider.off("status", this.#onProviderStatus);
    this.#provider.offSynced(this.#onProviderSynced);
    this.#provider.awareness.off("change", this.#onAwarenessChange);
  }

  #emit(): void {
    if (this.#destroyed) return;
    const snapshot = this.snapshot;
    for (const listener of this.#listeners) listener(snapshot);
  }
}

export function providerTarget(
  yjsUrl: string,
  params: WhiteboardProviderTarget["params"],
): WhiteboardProviderTarget {
  const url = new URL(yjsUrl);
  const segments = url.pathname.split("/").filter(Boolean);
  const roomName = segments.pop();
  if (roomName !== "yjs" || segments.length !== 0)
    throw new Error("whiteboard provider requires the canonical /yjs endpoint");
  url.pathname = "";
  url.search = "";
  url.hash = "";
  return {
    serverUrl: url.toString().replace(/\/$/u, ""),
    roomName,
    params,
  };
}

function createProvider(
  target: WhiteboardProviderTarget,
  doc: Y.Doc,
  awareness: awarenessProtocol.Awareness,
): WhiteboardProviderLike {
  const provider = new WebsocketProvider(
    target.serverUrl,
    target.roomName,
    doc,
    {
      params: { ...target.params },
      connect: false,
      disableBc: true,
      awareness,
    },
  );
  return {
    awareness: provider.awareness,
    get ws() {
      return provider.ws;
    },
    get wsconnected() {
      return provider.wsconnected;
    },
    get readyState() {
      return provider.ws?.readyState;
    },
    get synced() {
      return provider.synced;
    },
    connect: () => provider.connect(),
    disconnect: () => provider.disconnect(),
    destroy: () => provider.destroy(),
    on: (event, listener) => provider.on(event, listener),
    off: (event, listener) => provider.off(event, listener),
    onSynced: (listener) => provider.on("sync", listener),
    offSynced: (listener) => provider.off("sync", listener),
  };
}

function createDataChannelProvider(
  carrier: W10MemberPrimaryCarrier,
  identity: W10MemberCarrierIdentity,
  doc: Y.Doc,
  onRecoverableFailure: (failure: W10WhiteboardRecoverableFailure) => void,
  onProtocolFailure: (
    reason: W10PageHardCause | "Y_PROTOCOL_INTEGRITY",
  ) => void,
  awareness: awarenessProtocol.Awareness,
  onAwarenessRebuilt: (
    previous: awarenessProtocol.Awareness,
    next: awarenessProtocol.Awareness,
  ) => void,
): WhiteboardProviderLike {
  return new W10WhiteboardProvider(doc, carrier, identity, {
    connect: false,
    onRecoverableFailure,
    onProtocolError: () => onProtocolFailure("Y_PROTOCOL_INTEGRITY"),
    onControlProtocolError: () =>
      onProtocolFailure("CONTROL_PROTOCOL_INTEGRITY"),
    onAwarenessRebuilt,
    awareness,
  });
}

function persistenceName(sessionId: string): string {
  return `klass-share.whiteboard.v1.${sessionId}`;
}

function stableColorSeed(clientId: string): number {
  let hash = 0;
  for (const character of clientId) {
    const codePoint = character.codePointAt(0);
    if (codePoint !== undefined) hash = (hash * 31 + codePoint) | 0;
  }
  return hash;
}

export { DEFAULT_WHITEBOARD_PAGE_ID };
